import PropTypes from 'prop-types';
import React from 'react';
import { get } from 'lodash/fp';
import computeInvestorProfile from 'common/model/rip/investorProfile';
import { familySituations } from 'common/choices';
import lifePlansTypes from './profile/LifePlansTypes';
import { FaIcon } from '../../common/components/fa';
import './summaryState.scss';
import { isRipSigned } from '../../../../common/src/utils/signature/ripSignatureStatus';

const profiles = {
    P: {
        label: 'Prudent',
    },
    E: {
        label: 'Équilibré',
    },
    D: {
        label: 'Dynamique',
    },
};

const NC = () => <span className="summary-state-nc">NC</span>;

const dotModifier = (value) => {
    if (undefined === value) {
        return 'undefined';
    }

    return value;
};

const SummaryState = ({ rip, hasDerActive, visible }) => {
    const {
        firstName, lastName,
        investorProfiles,
        familySituation,
        lifePlans,
        errorCount,
    } = rip || {};

    const objectiveLabels = (lifePlans || [])
        .map(({ type }) => lifePlansTypes[type] && lifePlansTypes[type].title)
        .filter(Boolean);
    const investorProfile = computeInvestorProfile(get('client', investorProfiles));

    const profileLabel =
    'declined' === investorProfile
        ? 'Décliné'
        : (profiles[investorProfile] && profiles[investorProfile].label) || null;

    const fullName = [firstName, lastName].filter(Boolean).join(' ') || null;
    const ripSigned = isRipSigned(rip);

    const getCompletudeValue = () => {
        if (errorCount > 0) { return 'missing'; }
        if (!ripSigned) { return 'incomplete'; }

        return 'complete';
    };

    const getDerStatus = () => {
        if (!ripSigned) { return 'missing'; }
        if (ripSigned && !hasDerActive) { return 'incomplete'; }

        return 'complete';
    };

    const regItems = [
        { label: 'Complétude du RIP', value: getCompletudeValue() },
        { label: 'Questionnaire de risque', value: investorProfile ? 'complete' : 'missing' },
        { label: 'DER', value: getDerStatus() },
        { label: 'Lettre de mission', value: undefined },
        { label: "Pièce d'identité", value: undefined },
        { label: 'LCBFT LAB', value: undefined },
    ];

    return (
        <tr className="summary-state-row">
            <td colSpan={10}>
                <div className={`summary-state-slide${visible ? ' summary-state-slide--visible' : ''}`}>
                    <div className="summary-background">
                        <div className="summary-state-grid">

                            {/* Colonne 1 — Informations */}
                            <div className="summary-state-column">
                                <p className="summary-state-section-title">Informations</p>
                                <Field icon="user" label="Nom">{fullName || <NC />}</Field>
                                <Field icon="bar-chart" label="Profil investisseur">{profileLabel || <NC />}</Field>
                                <Field icon="users" label="Situation familiale">{familySituations[familySituation] || <NC />}</Field>
                            </div>

                            {/* Colonne 2 — Réglementation */}
                            <div className="summary-state-column summary-state-column--bordered">
                                <p className="summary-state-section-title">Réglementation</p>
                                {regItems.map(({ label, value }) => (
                                    <div
                                        key={label}
                                        className={`summary-state-reg-item${undefined === value ? ' summary-state-reg-item--disabled' : ''}`}
                                    >
                                        {label}
                                        <span className={`summary-state-dot summary-state-dot--${dotModifier(value)}`} />
                                    </div>
                                ))}
                            </div>

                            {/* Colonne 3 — Objectifs */}
                            <div className="summary-state-column summary-state-column--bordered">
                                <p className="summary-state-section-title">Objectifs</p>
                                {objectiveLabels.length > 0
                                    ? objectiveLabels.map((label) => (
                                        <div key={label} className="summary-state-objective">
                                            {label}
                                        </div>
                                    ))
                                    : <NC />}
                            </div>
                        </div>
                    </div>
                </div>
            </td>
        </tr>
    );
};

const Field = ({ icon, label, children }) => (
    <div className="summary-state-field">
        <div className="summary-state-field__left">
            {icon && <FaIcon icon={icon} className="summary-state-field__icon" />}
            <div className="summary-state-field__label">{label}</div>
        </div>
        <div className="summary-state-field__value">{children}</div>
    </div>
);

Field.propTypes = {
    icon: PropTypes.string,
    label: PropTypes.string,
    children: PropTypes.node,
};

SummaryState.propTypes = {
    rip: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        investorProfile: PropTypes.string,
        familySituation: PropTypes.string,
        signatureCount: PropTypes.number,
        signatureTotal: PropTypes.number,
        completude: PropTypes.string,
        questionnaire: PropTypes.string,
        lcbft: PropTypes.string,
        der: PropTypes.string,
        lettreMission: PropTypes.string,
        pieceIdentite: PropTypes.string,
        lifePlans: PropTypes.arrayOf(PropTypes.shape({ type: PropTypes.string })),
        state: PropTypes.string,
    }),
    hasDerActive: PropTypes.bool,
    visible: PropTypes.bool,
};

export default SummaryState;
